import React, { useContext, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { REQUEST_RESET, RESET_PASSWORD } from '../../graphql/mutations';
import { UserContext } from './auth-context';

export default function PasswordRecoveryForm({ initialEmail = '' }) {
  const navigate = useNavigate();
  const { logout } = useContext(UserContext);
  const [link] = useState(() => {
    const values = new URLSearchParams(window.location.hash.slice(1));
    return { email: values.get('email') || '', token: values.get('token') || '' };
  });
  React.useEffect(() => {
    if (link.token) navigate(window.location.pathname + window.location.search, { replace: true });
  }, [link.token, navigate]);
  const [email, setEmail] = useState(initialEmail || link.email);
  const [code, setCode] = useState(link.token);
  const [stage, setStage] = useState(link.token ? 'reset' : 'request');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [request, { loading: requesting }] = useMutation(REQUEST_RESET);
  const [reset, { loading: resetting }] = useMutation(RESET_PASSWORD);
  const busy = requesting || resetting;
  const submit = async event => {
    event.preventDefault(); setError('');
    const normalizedEmail = email.trim().toLowerCase();
    if (stage === 'request') {
      try { await request({ variables: { email: normalizedEmail } }); setCode(''); setStage('reset'); }
      catch { setError('Could not request a reset. Please try again shortly.'); }
      return;
    }
    if (password !== confirm) { setError('The passwords do not match.'); return; }
    try {
      await reset({ variables: { email: normalizedEmail, password, resetToken: code.trim() } });
      setCode(''); setPassword(''); setConfirm(''); setStage('done');
    } catch (failure) { setError(failure.graphQLErrors?.[0]?.message || 'Could not reset your password. Please try again.'); }
  };
  return <Box component="form" onSubmit={submit} sx={{ width: '100%' }}>
    <Stack spacing={2}>
      <Typography variant="h6">{stage === 'done' ? 'Password updated' : 'Reset your password'}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {stage === 'done' ? <>
        <Alert severity="success">Your password is updated. Sign in again with your new password.</Alert>
        <Button variant="contained" onClick={() => { logout?.(); navigate('/login'); }}>Go to sign in</Button>
      </> : <>
        <Typography color="text.secondary" variant="body2">
          {stage === 'request' ? 'Enter your account email. We will email you a secure reset link and code.' : 'If an active account matches that address, a reset email is on its way. Check your inbox and spam folder. The link and code expire in 30 minutes.'}
        </Typography>
        <TextField label="Account email" type="email" required autoComplete="email" value={email}
          disabled={busy || stage === 'reset'} onChange={event => setEmail(event.target.value)} />
        {stage === 'reset' && <>
          <TextField label="Reset code from your email" required autoComplete="one-time-code" value={code}
            onChange={event => setCode(event.target.value)} disabled={busy} inputProps={{ maxLength: 64, spellCheck: false }} />
          <TextField label="New password" type="password" required autoComplete="new-password" value={password}
            onChange={event => setPassword(event.target.value)} disabled={busy}
            helperText="Use at least 8 characters, including uppercase, lowercase, a number and a symbol." />
          <TextField label="Confirm new password" type="password" required autoComplete="new-password" value={confirm}
            onChange={event => setConfirm(event.target.value)} disabled={busy} />
        </>}
        <Button type="submit" variant="contained" disabled={busy}>
          {busy ? 'Please wait...' : stage === 'request' ? 'Email reset link' : 'Reset password'}
        </Button>
        {stage === 'reset' && <Button disabled={busy} onClick={() => { setStage('request'); setError(''); setCode(''); }}>Use another email or request a new link</Button>}
        <Typography variant="caption" color="text.secondary">Signed up with Google? You can also use Sign in with Google.</Typography>
      </>}
    </Stack>
  </Box>;
}
